/** Sort direction of a table column: `1` ascending, `-1` descending. */
export type BdTableSortOrder = 1 | -1;

/** A table's single-column default sort, e.g. `{ field: 'year', order: -1 }`. */
export interface BdTableSort {
  /** Row property to sort by; dotted paths (`ist.caseCount`) reach nested values. */
  field: string;
  order: BdTableSortOrder;
}

/**
 * Returns a sorted COPY of `rows`, ordered the way a PrimeNG `p-table` orders
 * them for the same `sortField` / `sortOrder` (nulls first when ascending,
 * strings via `localeCompare`, everything else via `<` / `>`; ties keep their
 * input order).
 *
 * Exports use this to follow the table's default order explicitly. Since
 * PrimeNG 22 the table no longer leaves the array it was given in its display
 * order — it sorts that array ascending in place and only its internal copy
 * descending — so an export that reads the same array must not rely on it
 * (BDA-6345).
 */
export function sortRowsLikeTable<T>(
  rows: readonly T[],
  sort: BdTableSort,
): T[] {
  return [...rows].sort(
    (a, b) =>
      sort.order *
      compareTableValues(
        resolveField(a, sort.field),
        resolveField(b, sort.field),
      ),
  );
}

function compareTableValues(a: unknown, b: unknown): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return -1;
  }
  if (b == null) {
    return 1;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b);
  }

  return a < b ? -1 : a > b ? 1 : 0;
}

function resolveField(row: unknown, field: string): unknown {
  return field
    .split('.')
    .reduce<unknown>(
      (value, key) =>
        value == null ? undefined : (value as Record<string, unknown>)[key],
      row,
    );
}
